import { Ticket } from "@/sharedTypes";
import { baseApi } from "../baseApi";
import { flattenQueryParams } from "@/utils/requestUtils"; // Import the utility function

const ticketTag = "Ticket";

const ticketApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createTicket: builder.mutation({
      query: (payload: Ticket) => ({
        url: "/gw/tickets/create/agent",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [ticketTag],
    }),
    getTickets: builder.query<any, Record<string, any> | void>({
      query: (params) => {
        const queryString = params
          ? `?${new URLSearchParams(flattenQueryParams(params)).toString()}`
          : "";
        return `/gw/tickets${queryString}`;
      },
      providesTags: [ticketTag],
    }),
    getTicketMails: builder.query<any, string | number>({
      query: (ticketId) => `/gw/tickets/${ticketId}/messages`,
    }),
    sendMailReply: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/reply`,
        method: "POST",
        body: payload,
      }),
    }),
    getTicketById: builder.query({
      query: (ticketId) => `/gw/tickets/${ticketId}`,
      transformResponse: (response: any) => response?.ticket,
    }),

    getTicketHistory: builder.query<
      any,
      { ticketId: string; queryParams?: Record<string, any> }
    >({
      query: ({ ticketId, queryParams }) => {
        const queryString = queryParams
          ? `&${new URLSearchParams(flattenQueryParams(queryParams)).toString()}`
          : "";
        return `/gw/ticket-histories?ticket_id=${ticketId}${queryString}`;
      },
      providesTags: (result, error, { ticketId }) => [
        { type: "TicketHistory", id: ticketId },
      ],
    }),

    updateTicket: builder.mutation({
      query: ({ ticketId, ...payload }: Ticket & { ticketId: number }) => ({
        url: `/gw/tickets/update/${ticketId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: "TicketHistory", id: ticketId },
        ticketTag,
      ],
    }),
    deleteTicket: builder.mutation<void, string>({
      query: (ticketId) => ({
        url: `/gw/tickets/delete/${ticketId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ticketTag],
    }),
    // Add permanent delete and restore endpoints
    permanentDeleteTicket: builder.mutation<void, string>({
      query: (ticketId) => ({
        url: `/gw/tickets/${ticketId}/permanent`,
        method: "DELETE",
      }),
      invalidatesTags: [ticketTag],
    }),
    restoreTicket: builder.mutation<void, string>({
      query: (ticketId) => ({
        url: `/gw/tickets/${ticketId}/restore`,
        method: "PUT",
      }),
      invalidatesTags: [ticketTag],
    }),
  }),
});

export const {
  useCreateTicketMutation,
  useGetTicketByIdQuery,
  useGetTicketsQuery,
  useGetTicketMailsQuery,
  useSendMailReplyMutation,
  useGetTicketHistoryQuery,
  useUpdateTicketMutation,
  useDeleteTicketMutation,
  usePermanentDeleteTicketMutation,
  useRestoreTicketMutation,
} = ticketApi;
